import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { formatEuro, formatInteger } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import {
  Banner,
  Button,
  Card,
  FilterChip,
  Pill,
  SearchField,
  Segmented,
  Select,
  Table,
  TableSkeleton,
  TextLink,
  type TableColumn,
} from '../../../ds/components';
import { useIsCompact, useIsMobile } from '../../../ds/hooks';
import { errorMessage } from '../../../lib/api';
import { useDealFilters, useDealList } from '../api';
import { VALUE_MASKED_TEXT } from '../board/BoardCard';
import { DealsTabs, type DealsTab } from '../DealsTabs';
import { ageText, sumWithEuro } from '../format';
import type { DealFilter, ListRow, ListSort, ListStatus } from '../types';
import pageStyles from '../DealsPage.module.css';
import styles from './list.module.css';

/**
 * Deals 01/02: the deal list with the filter pills (Alle aktiv · Meine · Gate-Warnung · Überfällig ·
 * Verloren), the sort control („Tage im Stage" · Kaufpreis · Kontakt) and the designed empty card.
 */

export interface DealsListProps {
  filter: DealFilter;
  onFilterChange: (filter: DealFilter) => void;
  status: ListStatus;
  onStatusChange: (status: ListStatus) => void;
  sort: ListSort;
  onSortChange: (sort: ListSort) => void;
  pipelineKind: 'property' | 'financing';
  onPipelineKindChange: (kind: 'property' | 'financing') => void;
  onTabChange: (tab: DealsTab) => void;
  onCreate: () => void;
}

type PillKey = 'all' | 'mine' | 'gate' | 'overdue' | 'lost';

const SORT_OPTIONS: { value: ListSort; label: string }[] = [
  { value: 'stageAge', label: 'Tage im Stage' },
  { value: 'value', label: 'Kaufpreis' },
  { value: 'contact', label: 'Kontakt' },
];

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function activePill(filter: DealFilter, status: ListStatus): PillKey {
  if (status === 'lost') return 'lost';
  if (filter.mine) return 'mine';
  if (filter.gateOnly) return 'gate';
  if (filter.overdueOnly) return 'overdue';
  return 'all';
}

export function DealsList({
  filter,
  onFilterChange,
  status,
  onStatusChange,
  sort,
  onSortChange,
  pipelineKind,
  onPipelineKindChange,
  onTabChange,
  onCreate,
}: DealsListProps) {
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const compact = useIsCompact();
  const [search, setSearch] = useState(filter.q ?? '');
  const debouncedSearch = useDebounced(search.trim(), 350);
  useEffect(() => {
    if (debouncedSearch === (filter.q ?? '')) return;
    onFilterChange({ ...filter, q: debouncedSearch || undefined });
  }, [debouncedSearch]);
  useEffect(() => {
    setSearch(filter.q ?? '');
  }, [filter.q]);

  const property = pipelineKind === 'property';
  const list = useDealList(filter, status, sort);
  const filters = useDealFilters(property);
  const items = useMemo(() => list.data?.pages.flatMap((page) => page.items) ?? [], [list.data]);
  const first = list.data?.pages[0];
  const counts = first?.counts;
  const pill = activePill(filter, status);
  const propertyName = filters.data?.properties.find((entry) => entry.id === filter.propertyId)?.name ?? null;
  const partnerName =
    filters.data?.referrerPartners?.find((entry) => entry.id === filter.referrerPartnerId)?.name ?? null;
  const isFiltered = Boolean(
    filter.mine ||
    filter.gateOnly ||
    filter.overdueOnly ||
    filter.propertyId ||
    filter.advisorId ||
    filter.referrerPartnerId ||
    filter.q ||
    status === 'lost',
  );

  function selectPill(key: PillKey) {
    const base: DealFilter = {
      q: filter.q,
      propertyId: filter.propertyId,
      advisorId: filter.advisorId,
      referrerPartnerId: filter.referrerPartnerId,
    };
    onStatusChange(key === 'lost' ? 'lost' : 'active');
    onFilterChange({
      ...base,
      mine: key === 'mine' || undefined,
      gateOnly: key === 'gate' || undefined,
      overdueOnly: key === 'overdue' || undefined,
    });
  }

  const columns = useMemo<TableColumn<ListRow>[]>(() => {
    const all: TableColumn<ListRow>[] = [
      {
        id: 'contact',
        header: 'Kontakt',
        track: 'minmax(0,1.2fr)',
        cell: (row) => <span className={styles.name}>{row.contactName}</span>,
      },
      {
        id: 'unit',
        header: 'Einheit · Objekt',
        track: 'minmax(0,1.5fr)',
        cell: (row) => <span className={styles.muted}>{row.unitLine}</span>,
      },
      {
        id: 'price',
        header: 'Kaufpreis',
        track: '110px',
        numeric: true,
        cell: (row) =>
          row.priceMasked ? (
            <span className={styles.priceMasked}>{VALUE_MASKED_TEXT}</span>
          ) : (
            <span className={styles.price}>{row.priceCents === null ? '—' : formatEuro(row.priceCents)}</span>
          ),
      },
      {
        id: 'stage',
        header: 'Stage',
        track: '130px',
        cell: (row) => <Pill variant={row.stage.tone}>{row.stage.name}</Pill>,
      },
      {
        id: 'closer',
        header: 'Closer',
        track: '100px',
        cell: (row) => <span className={styles.muted}>{row.closerShort ?? '—'}</span>,
      },
      {
        id: 'days',
        header: 'Tage',
        track: '80px',
        numeric: true,
        cell: (row) => (
          <span className={clsx(styles.days, row.overdue && styles.daysOverdue)}>{ageText(row.ageDays)}</span>
        ),
      },
      {
        id: 'next',
        header: 'Nächster Schritt',
        track: 'minmax(0,1fr)',
        cell: (row) => (
          <span className={clsx(styles.nextStep, row.nextStep === 'überfällig' && styles.nextStepOverdue)}>
            {row.nextStep}
          </span>
        ),
      },
    ];
    return compact ? all.filter((column) => column.id !== 'closer') : all;
  }, [compact]);

  const summary = first
    ? `${formatInteger(first.summary.activeDeals)} aktiv · ${first.summary.valueCents === null ? 'Summen ausgeblendet' : `${sumWithEuro(first.summary.valueCents)} Kaufpreis`}`
    : null;
  const headSummary =
    first && isFiltered && first.total !== first.summary.activeDeals && status === 'active'
      ? `${formatInteger(first.total)} von ${formatInteger(first.summary.activeDeals)}`
      : summary;

  const head = (
    <div className={pageStyles.listHead}>
      <div className={pageStyles.listHeadText}>
        <h1 className={pageStyles.listTitle}>Deals</h1>
        {headSummary ? <div className={pageStyles.listCount}>{headSummary}</div> : null}
      </div>
      <div className={pageStyles.headRight}>
        {property ? (
          <SearchField
            label="Deals durchsuchen"
            placeholder="Kontakt, Einheit, Objekt"
            icon={false}
            className={clsx(pageStyles.search, pageStyles.searchWide)}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        ) : null}
        {!mobile ? (
          <Segmented
            label="Pipeline"
            value={pipelineKind}
            items={[
              { value: 'property', label: 'Immobilie' },
              { value: 'financing', label: 'Finanzierung' },
            ]}
            onChange={onPipelineKindChange}
          />
        ) : null}
        {property ? <Button onClick={onCreate}>Deal anlegen</Button> : null}
      </div>
    </div>
  );

  if (!property) {
    return (
      <div className={pageStyles.page} style={{ padding: '24px 32px 32px', gap: 18 }}>
        {head}
        <DealsTabs value="list" onChange={onTabChange} />
        <div className={styles.empty}>
          <div className={styles.emptyTitle}>Noch keine Finanzierungen.</div>
          <p className={styles.emptyText}>
            Die Liste „Finanzierung“ kommt mit dem Finanzierungsmodul. Bis dahin führen Sie Finanzierungen in
            der Deal-Akte.
          </p>
          <div className={styles.emptyActions}>
            <Button variant="secondary" onClick={() => onPipelineKindChange('property')}>
              Zur Liste Immobilie
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const tracks = columns.map((column) => column.track ?? 'minmax(0,1fr)');

  return (
    <div className={pageStyles.page} style={{ padding: mobile ? undefined : '24px 32px 32px', gap: 18 }}>
      {head}
      <DealsTabs value="list" onChange={onTabChange} />
      <div className={styles.pillsRow} role="group" aria-label="Filter">
        <FilterChip
          label="Alle aktiv"
          count={counts ? formatInteger(counts.active) : undefined}
          active={pill === 'all'}
          onClick={() => selectPill('all')}
        />
        <FilterChip
          label="Meine"
          count={counts ? formatInteger(counts.mine) : undefined}
          active={pill === 'mine'}
          onClick={() => selectPill('mine')}
        />
        <FilterChip
          label="Gate-Warnung"
          count={counts ? formatInteger(counts.gate) : undefined}
          active={pill === 'gate'}
          onClick={() => selectPill('gate')}
        />
        <FilterChip
          label="Überfällig"
          count={counts ? formatInteger(counts.overdue) : undefined}
          active={pill === 'overdue'}
          onClick={() => selectPill('overdue')}
        />
        <FilterChip
          label="Verloren"
          count={counts ? formatInteger(counts.lost) : undefined}
          active={pill === 'lost'}
          onClick={() => selectPill('lost')}
        />
        {propertyName ? (
          <FilterChip
            label={`Objekt: ${propertyName}`}
            active
            onClick={() => onFilterChange({ ...filter, propertyId: undefined })}
            onRemove={() => onFilterChange({ ...filter, propertyId: undefined })}
          />
        ) : null}
        {filter.referrerPartnerId ? (
          <FilterChip
            label={`Tippgeber-Partner: ${partnerName ?? '…'}`}
            active
            onClick={() => onFilterChange({ ...filter, referrerPartnerId: undefined })}
            onRemove={() => onFilterChange({ ...filter, referrerPartnerId: undefined })}
          />
        ) : null}
        {!mobile ? (
          <div className={styles.sort}>
            <span>Sortiert nach</span>
            <Select
              label="Sortierung"
              labelHidden
              size="compact"
              className={styles.sortSelect}
              value={sort}
              options={SORT_OPTIONS}
              onChange={(event) => onSortChange(event.target.value as ListSort)}
            />
          </div>
        ) : null}
      </div>

      {list.isError ? (
        <Banner
          tone="danger"
          actions={
            <Button variant="secondary" size="small" onClick={() => void list.refetch()}>
              Erneut laden
            </Button>
          }
        >
          {errorMessage(list.error)}
        </Banner>
      ) : list.isPending ? (
        mobile ? (
          <TableSkeleton columns={['minmax(0,1fr)']} rows={6} headerTone="plain" />
        ) : (
          <Card flush className={styles.tableCard}>
            <TableSkeleton columns={tracks} rows={7} headerTone="plain" />
          </Card>
        )
      ) : items.length === 0 ? (
        <div className={styles.empty}>
          <div className={styles.emptyTitle}>Keine Deals mit diesen Filtern.</div>
          {first?.emptyHint ? <p className={styles.emptyText}>{first.emptyHint}</p> : null}
          <div className={styles.emptyActions}>
            {filter.mine ? (
              <Button variant="secondary" onClick={() => onFilterChange({ ...filter, mine: undefined })}>
                Filter „Meine“ entfernen
              </Button>
            ) : null}
            <TextLink
              as="button"
              onClick={() => {
                onStatusChange('active');
                onFilterChange({});
              }}
            >
              Alle Filter löschen
            </TextLink>
          </div>
        </div>
      ) : mobile ? (
        <div className={styles.mobileList}>
          {items.map((row) => (
            <button
              key={row.id}
              type="button"
              className={styles.mobileRow}
              onClick={() => void navigate(`/deals/${row.id}`)}
            >
              <span className={styles.mobileTop}>
                <span className={styles.name}>{row.contactName}</span>
                <span className={styles.mobileValue}>
                  {row.priceMasked ? '—' : row.priceCents === null ? '—' : formatEuro(row.priceCents)}
                </span>
              </span>
              <span className={styles.muted}>{row.unitLine}</span>
              <span className={styles.mobileBottom}>
                <Pill variant={row.stage.tone}>{row.stage.name}</Pill>
                <span className={clsx(styles.days, row.overdue && styles.daysOverdue)}>
                  {ageText(row.ageDays)}
                </span>
                <span
                  className={clsx(styles.nextStep, row.nextStep === 'überfällig' && styles.nextStepOverdue)}
                >
                  {row.nextStep}
                </span>
              </span>
            </button>
          ))}
        </div>
      ) : (
        <Card flush className={styles.tableCard}>
          <Table
            label="Deals"
            columns={columns}
            rows={items}
            rowKey={(row) => row.id}
            headerTone="plain"
            onRowClick={(row) => void navigate(`/deals/${row.id}`)}
          />
        </Card>
      )}

      {list.hasNextPage ? (
        <div className={styles.more}>
          <Button
            variant="secondary"
            loading={list.isFetchingNextPage}
            onClick={() => void list.fetchNextPage()}
          >
            Weitere laden
          </Button>
        </div>
      ) : null}
    </div>
  );
}
